import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
  Dropdown,
  Option,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  SearchBox,
  Label,
  Checkbox,
  Input
} from '@fluentui/react-components';
import { ArrowDownload20Regular, PlayRegular, DismissRegular, EyeRegular, CodeRegular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('0', '24px'),
    maxWidth: '100%',
    minWidth: '0',
    overflow: 'hidden',
  },
  section: {
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    maxWidth: '100%',
    minWidth: '0',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  tabList: {
    marginBottom: '24px',
  },
  tabContent: {
    ...shorthands.padding('16px', '0'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: 'calc(100vh - 400px)',
    maxWidth: '100%',
    minWidth: '0',
    overflow: 'hidden',
  },
  placeholder: {
    padding: '24px',
    color: tokens.colorNeutralForeground1,
  },
  controlsBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    maxWidth: '100%',
    minWidth: '0',
    flexWrap: 'wrap',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '150px',
    maxWidth: '250px',
    flex: '0 1 auto',
  },
  controlGroupWide: {
    flex: 1,
    minWidth: '300px',
  },
  tableContainer: {
    flex: 1,
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    overflowX: 'auto',
    overflowY: 'auto',
    width: '100%',
    maxWidth: '100%',
  },
  tableWrapper: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  tableControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dataTable: {
    width: 'max-content',
    backgroundColor: tokens.colorNeutralBackground1,
    borderCollapse: 'collapse',
  },
  tableCell: {
    minWidth: '120px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...shorthands.padding('8px', '12px'),
  },
  tableHeaderCell: {
    minWidth: '120px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    ...shorthands.padding('8px', '12px'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground2,
    fontSize: '14px',
  },
  seederLayout: {
    display: 'flex',
    gap: '16px',
    height: '100%',
  },
  seederColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: 0,
  },
  seederColumnLeft: {
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: 0,
  },
  seederColumnMiddle: {
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: 0,
  },
  seederColumnRight: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: 0,
  },
  controlCard: {
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  controlCardScrollable: {
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
  },
  objectsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  categoryHeader: {
    fontSize: '12px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '12px',
    marginBottom: '4px',
    ...shorthands.padding('4px', '8px'),
  },
  objectItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    ...shorthands.padding('8px'),
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  objectItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  objectInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  objectName: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  objectDescription: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  seederActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  seederStatus: {
    flex: 1,
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  statusTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '16px',
  },
  statusTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  statusLog: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    fontFamily: 'monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  previewTable: {
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    overflowY: 'auto',
    flex: 1,
  },
  previewTableContainer: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    minHeight: 0,
  },
  previewSection: {
    marginBottom: '24px',
  },
  previewSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
    marginBottom: '12px',
  },
  previewSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  previewSectionBadge: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  previewDataCard: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    marginBottom: '8px',
    fontSize: '12px',
  },
  previewDataRow: {
    display: 'flex',
    ...shorthands.padding('4px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      ...shorthands.borderBottom('0'),
    },
  },
  previewDataLabel: {
    fontWeight: '600',
    minWidth: '140px',
    color: tokens.colorNeutralForeground2,
  },
  previewDataValue: {
    flex: 1,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
  },
  previewNote: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    marginTop: '8px',
    ...shorthands.padding('8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  apiRequestContainer: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    marginTop: '16px',
  },
  apiRequestTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  apiRequestCode: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground3,
    fontFamily: 'monospace',
    fontSize: '11px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowX: 'auto',
  },
  checkbox: {
    marginTop: '2px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface DataRow {
  [key: string]: any;
}

const DbTools: React.FC = () => {
  const styles = useStyles();
  const [dbUtilityTab, setDbUtilityTab] = useState<string>('query-runner');

  // Data Browser state
  const [dataSource, setDataSource] = useState<string>('');
  const [table, setTable] = useState<string>('');
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Seeder Tool state
  const [seederService, setSeederService] = useState<string>('');
  const [selectedObjects, setSelectedObjects] = useState<Set<string>>(new Set());
  const [seederCount, setSeederCount] = useState<string>('10');
  const [seedingStatus, setSeedingStatus] = useState<string>('');
  const [isSeeding, setIsSeeding] = useState<boolean>(false);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [showApiRequest, setShowApiRequest] = useState<boolean>(false);

  // Mock data sources - these would come from your backend
  const dataSources = [
    { id: 'booqable', name: 'Booqable' },
    { id: 'linxup', name: 'Linxup' },
    { id: 'quickbooks', name: 'QuickBooks' },
    { id: 'local-db', name: 'Local Database' },
  ];

  // Mock tables for each data source - these would come from your backend
  const tablesBySource: Record<string, string[]> = {
    'booqable': ['customers', 'orders', 'products', 'inventory'],
    'linxup': ['vehicles', 'trips', 'locations', 'alerts'],
    'quickbooks': ['invoices', 'payments', 'customers', 'items'],
    'local-db': ['users', 'sessions', 'logs', 'settings'],
  };

  // Seedable objects by service - complete Booqable API objects
  const seedableObjectsByService: Record<string, { id: string; name: string; description: string; category: string }[]> = {
    'booqable': [
      // Inventory & Products
      { id: 'products', name: 'Products', description: 'Product catalog items', category: 'Inventory & Products' },
      { id: 'variants', name: 'Variants', description: 'Product variations', category: 'Inventory & Products' },
      { id: 'stock_items', name: 'Stock Items', description: 'Individual stock items', category: 'Inventory & Products' },
      { id: 'bundles', name: 'Bundles', description: 'Product bundles', category: 'Inventory & Products' },
      { id: 'bundle_items', name: 'Bundle Items', description: 'Items within bundles', category: 'Inventory & Products' },
      { id: 'collections', name: 'Collections', description: 'Product collections', category: 'Inventory & Products' },
      { id: 'clusters', name: 'Clusters', description: 'Product clusters', category: 'Inventory & Products' },
      { id: 'barcodes', name: 'Barcodes', description: 'Barcode identifiers', category: 'Inventory & Products' },

      // Customers & Users
      { id: 'customers', name: 'Customers', description: 'Customer records with contact information', category: 'Customers & Users' },
      { id: 'customer_groups', name: 'Customer Groups', description: 'Customer group segments', category: 'Customers & Users' },
      { id: 'users', name: 'Users', description: 'User accounts', category: 'Customers & Users' },
      { id: 'user_roles', name: 'User Roles', description: 'User permission roles', category: 'Customers & Users' },

      // Orders & Transactions
      { id: 'orders', name: 'Orders', description: 'Rental orders and bookings', category: 'Orders & Transactions' },
      { id: 'order_items', name: 'Order Items', description: 'Line items within orders', category: 'Orders & Transactions' },
      { id: 'invoices', name: 'Invoices', description: 'Invoice records', category: 'Orders & Transactions' },
      { id: 'payments', name: 'Payments', description: 'Payment transactions', category: 'Orders & Transactions' },
      { id: 'quotes', name: 'Quotes', description: 'Quote estimates', category: 'Orders & Transactions' },
      { id: 'reservations', name: 'Reservations', description: 'Item reservations', category: 'Orders & Transactions' },
      { id: 'checkins', name: 'Check-ins', description: 'Item check-in records', category: 'Orders & Transactions' },
      { id: 'checkouts', name: 'Check-outs', description: 'Item check-out records', category: 'Orders & Transactions' },

      // Scheduling & Availability
      { id: 'events', name: 'Events', description: 'Calendar events', category: 'Scheduling & Availability' },
      { id: 'availability', name: 'Availability', description: 'Product availability records', category: 'Scheduling & Availability' },
      { id: 'time_slots', name: 'Time Slots', description: 'Booking time slots', category: 'Scheduling & Availability' },
      { id: 'calendars', name: 'Calendars', description: 'Calendar configurations', category: 'Scheduling & Availability' },

      // Pricing & Discounts
      { id: 'price_lists', name: 'Price Lists', description: 'Pricing structures', category: 'Pricing & Discounts' },
      { id: 'discounts', name: 'Discounts', description: 'Discount rules', category: 'Pricing & Discounts' },
      { id: 'taxes', name: 'Taxes', description: 'Tax configurations', category: 'Pricing & Discounts' },
      { id: 'coupons', name: 'Coupons', description: 'Coupon codes', category: 'Pricing & Discounts' },

      // Locations & Logistics
      { id: 'locations', name: 'Locations', description: 'Store and warehouse locations', category: 'Locations & Logistics' },
      { id: 'shipping_methods', name: 'Shipping Methods', description: 'Available shipping methods', category: 'Locations & Logistics' },
      { id: 'shipping_rates', name: 'Shipping Rates', description: 'Shipping rate calculations', category: 'Locations & Logistics' },
      { id: 'delivery_windows', name: 'Delivery Windows', description: 'Delivery time windows', category: 'Locations & Logistics' },
    ],
    'linxup': [
      { id: 'vehicles', name: 'Vehicles', description: 'Vehicle fleet records', category: 'Fleet' },
      { id: 'drivers', name: 'Drivers', description: 'Driver profiles', category: 'Fleet' },
      { id: 'trips', name: 'Trips', description: 'Trip history records', category: 'Tracking' },
      { id: 'locations', name: 'Locations', description: 'GPS location points', category: 'Tracking' },
      { id: 'alerts', name: 'Alerts', description: 'Alert configurations', category: 'Monitoring' },
      { id: 'geofences', name: 'Geofences', description: 'Geofence boundaries', category: 'Monitoring' },
    ],
    'quickbooks': [
      { id: 'customers', name: 'Customers', description: 'Customer records', category: 'Contacts' },
      { id: 'invoices', name: 'Invoices', description: 'Invoice records', category: 'Transactions' },
      { id: 'payments', name: 'Payments', description: 'Payment transactions', category: 'Transactions' },
      { id: 'items', name: 'Items', description: 'Service and product items', category: 'Products' },
      { id: 'vendors', name: 'Vendors', description: 'Vendor records', category: 'Contacts' },
    ],
    'local-db': [
      { id: 'users', name: 'Users', description: 'Application user accounts', category: 'System' },
      { id: 'sessions', name: 'Sessions', description: 'User session data', category: 'System' },
      { id: 'logs', name: 'Logs', description: 'System log entries', category: 'System' },
      { id: 'settings', name: 'Settings', description: 'Application settings', category: 'System' },
    ],
  };

  const handleLoadData = async () => {
    if (!dataSource || !table) return;

    setIsLoading(true);

    try {
      if (dataSource === 'booqable') {
        // Map table names to Booqable API endpoints
        const endpointMap: Record<string, string> = {
          'customers': '/api/4/customers',
          'orders': '/api/4/orders',
          'products': '/api/4/product_groups',
          'inventory': '/api/4/stock_items',
        };

        const endpoint = endpointMap[table];
        if (!endpoint) {
          console.error('Unknown table:', table);
          setIsLoading(false);
          return;
        }

        // Make request to backend proxy with pagination
        const response = await apiFetch(`/booqable-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            method: 'GET',
            path: `${endpoint}?page[size]=100`,
          }),
        });

        const data = await response.json();

        if (data.data && Array.isArray(data.data)) {
          // Extract attributes from Booqable JSON:API format
          const records = data.data.map((item: any) => {
            const attributes = item.attributes || {};
            return {
              id: item.id,
              ...attributes,
            };
          });

          // Get column names from first record
          if (records.length > 0) {
            const columns = Object.keys(records[0]);
            setTableColumns(columns);
            setTableData(records);
          } else {
            setTableColumns([]);
            setTableData([]);
          }
        } else {
          setTableColumns([]);
          setTableData([]);
        }
      } else {
        // Mock data for other sources
        const mockColumns = ['id', 'name', 'status', 'created_at'];
        const mockData = Array.from({ length: 15 }, (_, i) => ({
          id: `${i + 1}`,
          name: `Sample Record ${i + 1}`,
          status: i % 2 === 0 ? 'Active' : 'Inactive',
          created_at: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
        }));

        setTableColumns(mockColumns);
        setTableData(mockData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setTableColumns([]);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (tableData.length === 0) return;

    const csv = [
      tableColumns.join(','),
      ...tableData.map(row => tableColumns.map(col => row[col]).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${table}_${dataSource}_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredData = tableData.filter(row => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(row).some(val =>
      String(val).toLowerCase().includes(query)
    );
  });

  const renderDataBrowser = () => {
    return (
      <div className={styles.tabContent}>
        <div className={styles.controlsBar}>
          <div className={styles.controlGroup}>
            <Label weight="semibold">Data Source</Label>
            <Dropdown
              placeholder="Select data source"
              value={dataSources.find(ds => ds.id === dataSource)?.name || ''}
              onOptionSelect={(_, data) => {
                setDataSource(data.optionValue as string);
                setTable('');
                setTableData([]);
                setTableColumns([]);
              }}
            >
              {dataSources.map(source => (
                <Option key={source.id} value={source.id}>
                  {source.name}
                </Option>
              ))}
            </Dropdown>
          </div>

          <div className={styles.controlGroup}>
            <Label weight="semibold">Table</Label>
            <Dropdown
              placeholder="Select table"
              value={table}
              disabled={!dataSource}
              onOptionSelect={(_, data) => {
                setTable(data.optionValue as string);
                setTableData([]);
                setTableColumns([]);
              }}
            >
              {dataSource && tablesBySource[dataSource]?.map(tbl => (
                <Option key={tbl} value={tbl}>
                  {tbl}
                </Option>
              ))}
            </Dropdown>
          </div>

          <Button
            appearance="primary"
            disabled={!dataSource || !table || isLoading}
            onClick={handleLoadData}
          >
            {isLoading ? 'Loading...' : 'Load Data'}
          </Button>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.tableTitle}>
              {tableData.length > 0 ? `${table} (${filteredData.length} records)` : 'No data loaded'}
            </div>
            <div className={styles.tableControls}>
              {tableData.length > 0 && (
                <>
                  <SearchBox
                    placeholder="Search records..."
                    value={searchQuery}
                    onChange={(e, data) => setSearchQuery(data?.value || '')}
                    size="small"
                  />
                  <Button
                    icon={<ArrowDownload20Regular />}
                    size="small"
                    onClick={handleExportCSV}
                  >
                    Export CSV
                  </Button>
                </>
              )}
            </div>
          </div>

          {tableData.length > 0 ? (
            <div className={styles.tableWrapper}>
              <Table className={styles.dataTable}>
                <TableHeader>
                  <TableRow>
                    {tableColumns.map(col => (
                      <TableHeaderCell key={col} className={styles.tableHeaderCell}>
                        {col}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((row, idx) => (
                    <TableRow key={idx}>
                      {tableColumns.map(col => (
                        <TableCell key={col} className={styles.tableCell}>
                          {String(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div>Select a data source and table, then click "Load Data" to view records</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleToggleObject = (objectId: string) => {
    const newSelected = new Set(selectedObjects);
    if (newSelected.has(objectId)) {
      newSelected.delete(objectId);
    } else {
      newSelected.add(objectId);
    }
    setSelectedObjects(newSelected);
  };

  const handleSelectAllObjects = () => {
    if (!seederService) return;
    const allObjects = seedableObjectsByService[seederService].map(obj => obj.id);
    setSelectedObjects(new Set(allObjects));
  };

  const handleDeselectAllObjects = () => {
    setSelectedObjects(new Set());
  };

  const generateMockData = (objectId: string, count: number): any[] => {
    const mockData: any[] = [];
    const timestamp = Date.now();

    // Generate realistic mock data based on object type
    for (let i = 1; i <= count; i++) {
      switch (objectId) {
        case 'customers':
          mockData.push({
            name: `Test Customer ${timestamp}-${i}`,
            company_name: `Test Company ${timestamp}-${i}`,
            email: `testcustomer${timestamp}${i}@ogilvie.io`,
            phone: `+1-555-010-${i.toString().padStart(4, '0')}`,
            mobile: `+1-555-020-${i.toString().padStart(4, '0')}`,
            website: `https://testcompany${timestamp}${i}.com`,
            notes: `Test customer created by seeder at ${new Date().toISOString()}`,
            reference: `TEST-${timestamp}-${i}`,
            discount: 0,
            send_invoice_email: true,
            send_booking_email: true,
            send_quote_email: true,
            send_order_confirmation_email: true,
            send_payment_confirmation_email: true,
            send_refund_confirmation_email: true,
            addresses: [
              {
                label: 'Billing Address',
                street: `${100 + i} Main St`,
                postal_code: `${10000 + i}`,
                city: 'Sample City',
                province: 'CA',
                country: 'United States',
              }
            ],
            custom_fields: {},
            tags: ['Test', 'Seeded'],
          });
          break;
        case 'products':
          mockData.push({
            name: `Test Product ${i}`,
            sku: `TEST-PROD-${i.toString().padStart(4, '0')}`,
            product_type: 'rental',
            tracking_type: 'trackable',
            trackable: true,
            has_variations: false,
            extra_information: `Sample product description ${i}`,
            show_in_store: true,
            price_type: 'simple',
            price_period: 'day',
          });
          break;
        case 'orders':
          mockData.push({
            order_number: `ORD-${i.toString().padStart(5, '0')}`,
            customer_id: `CUST-${i}`,
            status: ['pending', 'confirmed', 'active'][i % 3],
            start_date: new Date(Date.now() + i * 86400000).toISOString().split('T')[0],
            end_date: new Date(Date.now() + (i + 7) * 86400000).toISOString().split('T')[0],
            total: (Math.random() * 1000 + 100).toFixed(2),
          });
          break;
        case 'locations':
          mockData.push({
            name: `Test Warehouse ${i}`,
            code: `WH-${i}`,
            location_type: 'rental',
            address1: `${200 + i} Storage Blvd`,
            city: 'Distribution City',
            region: 'CA',
            zipcode: `${90000 + i}`,
            country: 'US',
          });
          break;
        case 'payments':
          mockData.push({
            payment_id: `PAY-${i.toString().padStart(5, '0')}`,
            order_id: `ORD-${i}`,
            amount: (Math.random() * 500 + 50).toFixed(2),
            method: ['credit_card', 'cash', 'bank_transfer'][i % 3],
            status: 'completed',
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
          });
          break;
        case 'users':
          // Users are employees in Booqable
          mockData.push({
            name: `Test Employee ${i}`,
            email: `testemployee${i}@company.com`,
            firstname: `First${i}`,
            lastname: `Last${i}`,
            active: true,
          });
          break;
        case 'stock_items':
          mockData.push({
            identifier: `TEST-ITEM-${i.toString().padStart(6, '0')}`,
            // Note: product_group_id would need to reference an actual product group
            // For seeding, you'd need to create product groups first
          });
          break;
        case 'invoices':
          mockData.push({
            invoice_number: `INV-${i.toString().padStart(5, '0')}`,
            order_id: `ORD-${i}`,
            amount: (Math.random() * 1000 + 100).toFixed(2),
            tax: (Math.random() * 100 + 10).toFixed(2),
            status: ['draft', 'sent', 'paid'][i % 3],
            due_date: new Date(Date.now() + i * 86400000).toISOString().split('T')[0],
          });
          break;
        default:
          // Generic object data
          mockData.push({
            id: `${objectId.toUpperCase()}-${i}`,
            name: `Test ${objectId} ${i}`,
            status: 'active',
            created_at: new Date().toISOString(),
          });
      }
    }

    return mockData;
  };

  const handlePreview = () => {
    if (selectedObjects.size === 0 || !seederService) return;

    setPreviewMode(true);
    setShowApiRequest(false); // Reset API request view

    // Generate preview data with actual mock records
    const objectsArray = Array.from(selectedObjects);
    const count = parseInt(seederCount) || 10;
    const preview: any[] = [];

    objectsArray.forEach(objectId => {
      const objectInfo = seedableObjectsByService[seederService].find(obj => obj.id === objectId);
      if (objectInfo) {
        const mockRecords = generateMockData(objectId, Math.min(count, 3)); // Show max 3 records in preview
        preview.push({
          objectType: objectInfo.name,
          objectId: objectId,
          category: objectInfo.category,
          totalRecords: count,
          sampleRecords: mockRecords,
        });
      }
    });

    setPreviewData(preview);
    setSeedingStatus(''); // Clear any previous status
  };

  const renderApiRequest = () => {
    if (!previewData || previewData.length === 0) return null;

    // Generate API request examples for each object type
    return (
      <div className={styles.apiRequestContainer}>
        {previewData.map((objectPreview, idx) => {
          const sampleRecord = objectPreview.sampleRecords[0];
          const apiPayload = {
            objectType: objectPreview.objectId,
            data: sampleRecord,
          };

          const booqablePayload = {
            data: {
              type: objectPreview.objectId,
              attributes: sampleRecord,
            },
          };

          return (
            <div key={idx} style={{ marginBottom: idx < previewData.length - 1 ? '24px' : '0' }}>
              <div className={styles.apiRequestTitle}>
                {objectPreview.objectType} - API Request (Sample)
              </div>
              <div style={{ marginBottom: '8px', fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
                POST /booqable-seed
              </div>
              <div className={styles.apiRequestCode}>
                {JSON.stringify(apiPayload, null, 2)}
              </div>
              <div style={{ marginTop: '12px', marginBottom: '8px', fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
                Backend sends to Booqable API:
              </div>
              <div className={styles.apiRequestCode}>
                {JSON.stringify(booqablePayload, null, 2)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleRunSeeder = async () => {
    if (selectedObjects.size === 0 || !seederService) return;

    setIsSeeding(true);
    setPreviewMode(false); // Exit preview mode
    setSeedingStatus('Starting seeding process...\n');

    const objectsArray = Array.from(selectedObjects);
    const count = parseInt(seederCount) || 10;
    let totalSuccess = 0;
    let totalFailed = 0;

    for (let i = 0; i < objectsArray.length; i++) {
      const objectId = objectsArray[i];
      const objectInfo = seedableObjectsByService[seederService].find(obj => obj.id === objectId);
      const objectName = objectInfo?.name || objectId;

      setSeedingStatus(prev =>
        prev + `\n[${new Date().toLocaleTimeString()}] Seeding ${objectName}... (${count} records)`
      );

      try {
        // Generate mock data for this object type
        const mockRecords = generateMockData(objectId, count);

        // Send each record to Booqable API
        let successCount = 0;
        let failCount = 0;

        for (let recordIdx = 0; recordIdx < mockRecords.length; recordIdx++) {
          const record = mockRecords[recordIdx];

          try {
            // Make API call to Booqable
            const response = await apiFetch(`/booqable-seed`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                objectType: objectId,
                data: record,
              }),
            });

            if (response.ok) {
              successCount++;
            } else {
              failCount++;
              let errorMessage = 'Unknown error';
              try {
                const errorData = await response.json();
                // Extract detailed error information
                if (errorData.error) {
                  errorMessage = errorData.error;
                }
                if (errorData.details) {
                  // Booqable API may return errors in details.errors array
                  if (errorData.details.errors && Array.isArray(errorData.details.errors)) {
                    const errors = errorData.details.errors.map((err: any) => {
                      if (err.detail) return err.detail;
                      if (err.title) return err.title;
                      return JSON.stringify(err);
                    }).join(', ');
                    errorMessage = `${errorMessage} - ${errors}`;
                  } else {
                    errorMessage = `${errorMessage} - ${JSON.stringify(errorData.details)}`;
                  }
                }
              } catch (e) {
                errorMessage = `HTTP ${response.status}`;
              }

              setSeedingStatus(prev =>
                prev + `\n  ⚠ Failed to create ${objectName} record ${recordIdx + 1}:\n     ${errorMessage}`
              );
            }
          } catch (error) {
            failCount++;
            setSeedingStatus(prev =>
              prev + `\n  ⚠ Error creating ${objectName} record ${recordIdx + 1}: ${String(error)}`
            );
          }
        }

        totalSuccess += successCount;
        totalFailed += failCount;

        if (failCount === 0) {
          setSeedingStatus(prev =>
            prev + `\n[${new Date().toLocaleTimeString()}] ✓ Successfully seeded ${successCount} ${objectName} records`
          );
        } else {
          setSeedingStatus(prev =>
            prev + `\n[${new Date().toLocaleTimeString()}] ⚠ Seeded ${successCount} ${objectName} records (${failCount} failed)`
          );
        }
      } catch (error) {
        totalFailed += count;
        setSeedingStatus(prev =>
          prev + `\n[${new Date().toLocaleTimeString()}] ✗ Failed to seed ${objectName}: ${error}`
        );
      }
    }

    setSeedingStatus(prev =>
      prev + `\n\n[${new Date().toLocaleTimeString()}] Seeding complete!\n` +
      `Total: ${objectsArray.length} object types\n` +
      `Success: ${totalSuccess} records\n` +
      `Failed: ${totalFailed} records`
    );

    setIsSeeding(false);
  };

  const renderSeederTool = () => {
    const availableObjects = seederService ? seedableObjectsByService[seederService] || [] : [];

    // Group objects by category
    const objectsByCategory: Record<string, typeof availableObjects> = {};
    availableObjects.forEach(obj => {
      if (!objectsByCategory[obj.category]) {
        objectsByCategory[obj.category] = [];
      }
      objectsByCategory[obj.category].push(obj);
    });

    return (
      <div className={styles.tabContent}>
        <div className={styles.seederLayout}>
          {/* Column 1: Service & Objects */}
          <div className={styles.seederColumnLeft}>
            <div className={styles.controlCard}>
              <div className={styles.cardTitle}>Service</div>
              <Dropdown
                placeholder="Select service"
                value={dataSources.find(ds => ds.id === seederService)?.name || ''}
                onOptionSelect={(_, data) => {
                  setSeederService(data.optionValue as string);
                  setSelectedObjects(new Set());
                  setSeedingStatus('');
                }}
              >
                {dataSources.map(source => (
                  <Option key={source.id} value={source.id}>
                    {source.name}
                  </Option>
                ))}
              </Dropdown>
            </div>

            {seederService && (
              <div className={styles.controlCardScrollable}>
                <div className={styles.cardTitle}>
                  Objects to Seed ({selectedObjects.size} selected)
                </div>
                <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <Button size="small" onClick={handleSelectAllObjects}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleDeselectAllObjects}>
                    Clear
                  </Button>
                </div>
                <div className={styles.objectsList}>
                  {Object.entries(objectsByCategory).map(([category, objects]) => (
                    <div key={category}>
                      <div className={styles.categoryHeader}>{category}</div>
                      {objects.map(obj => (
                        <div
                          key={obj.id}
                          className={`${styles.objectItem} ${selectedObjects.has(obj.id) ? styles.objectItemSelected : ''}`}
                          onClick={() => handleToggleObject(obj.id)}
                        >
                          <Checkbox
                            checked={selectedObjects.has(obj.id)}
                            onChange={() => handleToggleObject(obj.id)}
                            className={styles.checkbox}
                          />
                          <div className={styles.objectInfo}>
                            <div className={styles.objectName}>{obj.name}</div>
                            <div className={styles.objectDescription}>{obj.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Column 2: Configuration & Actions */}
          {seederService && (
            <div className={styles.seederColumnMiddle}>
              <div className={styles.controlCard}>
                <div className={styles.cardTitle}>Seeder Configuration</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Label weight="semibold">Records per object</Label>
                  <Input
                    type="number"
                    value={seederCount}
                    onChange={(e) => setSeederCount(e.target.value)}
                    min="1"
                    max="1000"
                  />
                </div>
              </div>

              <div className={styles.seederActions}>
                <Button
                  appearance="secondary"
                  icon={<EyeRegular />}
                  disabled={selectedObjects.size === 0 || isSeeding}
                  onClick={handlePreview}
                >
                  Preview
                </Button>
                <Button
                  appearance="primary"
                  icon={<PlayRegular />}
                  disabled={selectedObjects.size === 0 || isSeeding}
                  onClick={handleRunSeeder}
                >
                  {isSeeding ? 'Seeding...' : 'Run Seeder'}
                </Button>
                <Button
                  icon={<DismissRegular />}
                  disabled={!seedingStatus && !previewMode}
                  onClick={() => {
                    setSeedingStatus('');
                    setPreviewMode(false);
                    setPreviewData([]);
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Column 3: Status/Preview */}
          <div className={styles.seederColumnRight}>
            <div className={styles.seederStatus}>
              <div className={styles.statusTitleRow}>
                <div className={styles.statusTitle}>
                  {previewMode ? 'Preview - Objects to be Seeded' : 'Seeding Status'}
                </div>
                {previewMode && (
                  <Button
                    appearance="subtle"
                    icon={<CodeRegular />}
                    size="small"
                    onClick={() => setShowApiRequest(!showApiRequest)}
                  >
                    {showApiRequest ? 'Hide' : 'Show'} API Request
                  </Button>
                )}
              </div>
              {previewMode && previewData && previewData.length > 0 ? (
                <div className={styles.previewTableContainer}>
                  {!showApiRequest ? (
                    <>
                      {previewData.map((objectPreview, idx) => (
                        <div key={idx} className={styles.previewSection}>
                          <div className={styles.previewSectionHeader}>
                            <div className={styles.previewSectionTitle}>
                              {objectPreview.objectType} ({objectPreview.category})
                            </div>
                            <div className={styles.previewSectionBadge}>
                              {objectPreview.totalRecords} records
                            </div>
                          </div>
                          {objectPreview.sampleRecords && objectPreview.sampleRecords.map((record: any, recordIdx: number) => (
                            <div key={recordIdx} className={styles.previewDataCard}>
                              {Object.entries(record).map(([key, value]) => (
                                <div key={key} className={styles.previewDataRow}>
                                  <div className={styles.previewDataLabel}>{key}:</div>
                                  <div className={styles.previewDataValue}>{String(value)}</div>
                                </div>
                              ))}
                            </div>
                          ))}
                          {objectPreview.totalRecords > objectPreview.sampleRecords.length && (
                            <div className={styles.previewNote}>
                              Showing {objectPreview.sampleRecords.length} of {objectPreview.totalRecords} records
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    renderApiRequest()
                  )}
                </div>
              ) : previewMode && (!previewData || previewData.length === 0) ? (
                <div className={styles.emptyState}>
                  No preview data available
                </div>
              ) : seedingStatus ? (
                <div className={styles.statusLog}>{seedingStatus}</div>
              ) : (
                <div className={styles.emptyState}>
                  Select a service and objects, then click "Preview" or "Run Seeder"
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDatabaseUtilities = () => {
    return (
      <div className={styles.tabContent}>
        {dbUtilityTab === 'query-runner' && (
          <div className={styles.placeholder}>
            Query Runner content coming soon
          </div>
        )}
        {dbUtilityTab === 'data-browser' && renderDataBrowser()}
        {dbUtilityTab === 'seeder-tool' && renderSeederTool()}
        {dbUtilityTab === 'migration-trigger' && (
          <div className={styles.placeholder}>
            Migration Trigger content coming soon
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.content}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Database Utilities</div>
        <TabList
          className={styles.tabList}
          selectedValue={dbUtilityTab}
          onTabSelect={(_, data) => setDbUtilityTab(data.value as string)}
        >
          <Tab value="query-runner">Query Runner</Tab>
          <Tab value="data-browser">Data Browser</Tab>
          <Tab value="seeder-tool">Seeder Tool</Tab>
          <Tab value="migration-trigger">Migration Trigger</Tab>
        </TabList>
        {renderDatabaseUtilities()}
      </div>
    </div>
  );
};

export default DbTools;
